"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { NativeLink as Link } from "@/components/native-link";
import { Bot, BrainCircuit, Cloud, DollarSign, Headphones, Mic2, PieChart, RadioTower, RefreshCw, Timer, Video, Zap } from "lucide-react";
import { Card, MetricCard, PageHeader, Pill } from "@/components/pulse-shell";
import { Select } from "@/components/ui/select";
import { api, normalizeOverview, type Overview } from "@/lib/api";
import { useAuthProfile } from "@/lib/auth-context";
import { hasPermission } from "@/lib/users-rbac";

type RangeKey = "24h" | "7d" | "30d";
const RANGE_OPTS: Array<{ label: string; value: RangeKey }> = [
  { label: "24 hours", value: "24h" },
  { label: "7 days", value: "7d" },
  { label: "30 days", value: "30d" },
];

const emptyCharts = {
  Traffic: { pathA: "M0 180", pathB: "M0 180", maxLabel: "0", legendA: "Sessions", legendB: "With reply", labels: [] as string[] },
  Latency: { pathA: "M0 180", pathB: "M0 180", maxLabel: "0ms", legendA: "E2E avg", legendB: "Smoothed", labels: [] as string[] },
  Errors: { pathA: "M0 180", pathB: "M0 180", maxLabel: "0%", legendA: "Fail rate %", legendB: "Trend", labels: [] as string[] },
};

const mockOverview: Overview = normalizeOverview({
  concurrentSessions: 0,
  queueDepth: 0,
  workerCount: 1,
  e2eP50Ms: null,
  e2eP95Ms: null,
  spendTotalUsd: null,
  pipeline: [
    { name: "LiveKit", status: "healthy" },
    { name: "Deepgram", status: "healthy" },
    { name: "Groq", status: "healthy" },
    { name: "Cartesia", status: "healthy" },
    { name: "HeyGen", status: "healthy" },
    { name: "Storage", status: "healthy" },
  ],
  charts: emptyCharts,
  source: "mock",
});

function formatSpend(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return "$—";
  return usd < 10 ? `$${usd.toFixed(2)}` : `$${Math.round(usd)}`;
}

function formatMinuteCost(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return "$—";
  return `$${usd.toFixed(2)}/min`;
}

const pipeTone: Record<string, string> = {
  LiveKit: "livekit",
  Deepgram: "deepgram",
  Groq: "groq",
  Cartesia: "cartesia",
  HeyGen: "heygen",
  Storage: "storage",
};

export function OverviewPage() {
  const profile = useAuthProfile();
  const canViewFinance = hasPermission(profile?.permissions, "finance.view");
  const [mode, setMode] = useState<"Traffic" | "Latency" | "Errors">("Traffic");
  const [range, setRange] = useState<RangeKey>("24h");
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queuedHint, setQueuedHint] = useState(false);
  const [overview, setOverview] = useState<Overview>(mockOverview);
  const [spendBreakdown, setSpendBreakdown] = useState(false);
  const [oneMinBreakdown, setOneMinBreakdown] = useState(false);
  const [hover, setHover] = useState<{ i: number; x: number; label: string; a: number; b: number } | null>(null);
  const queuedSinceRef = useRef<string>("");

  async function load(nextRange = range) {
    setLoading(true);
    try {
      const live = await api.overview(nextRange);
      setOverview(normalizeOverview(live));
      setQueuedHint(false);
    } catch {
      setOverview({ ...mockOverview, source: "mock" });
    } finally {
      setLoading(false);
    }
  }

  async function scheduleRefresh() {
    if (queueing) return;
    setQueueing(true);
    queuedSinceRef.current = overview.generatedAt || "";
    setQueuedHint(true);
    try {
      await api.refreshOverview(range);
    } catch {
      setQueuedHint(false);
    } finally {
      setQueueing(false);
    }
  }

  useEffect(() => { void load(range); }, [range]);

  // After a manual queue, soft-poll until generatedAt advances (or give up).
  useEffect(() => {
    if (!queuedHint) return;
    const started = queuedSinceRef.current;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      void (async () => {
        try {
          const live = await api.overview(range);
          const next = normalizeOverview(live);
          if (next.generatedAt && next.generatedAt !== started) {
            setOverview(next);
            setQueuedHint(false);
          } else if (tries >= 12) {
            setQueuedHint(false);
          }
        } catch {
          if (tries >= 12) setQueuedHint(false);
        }
      })();
    }, 5000);
    return () => window.clearInterval(id);
  }, [queuedHint, range]);

  const p50Ms = overview.e2eP50Ms != null && Number.isFinite(overview.e2eP50Ms)
    ? Math.round(overview.e2eP50Ms)
    : null;
  const p95Ms = overview.e2eP95Ms != null && Number.isFinite(overview.e2eP95Ms)
    ? Math.round(overview.e2eP95Ms)
    : null;
  const p50 = p50Ms != null ? `${p50Ms}ms` : "—";
  const p95 = p95Ms != null ? `${p95Ms}ms` : "—";
  /** Post-EOS p50 SLA from LATENCY_FIREWALL.md — color the card by budget, not decorative .delta green. */
  const latencyTone =
    p50Ms == null ? "purple" : p50Ms <= 200 ? "green" : p50Ms <= 400 ? "orange" : "red";
  const latencyFoot =
    p50Ms == null ? (
      <span>post-EOS SLA p50 {"<"}200ms</span>
    ) : p50Ms <= 200 ? (
      <>
        <span className="sla-ok">Within SLA</span>
        <span>p95 {p95}</span>
      </>
    ) : (
      <>
        <span className={p50Ms <= 400 ? "sla-warn" : "sla-bad"}>
          {`${(p50Ms / 200).toFixed(1)}× over SLA (<200ms)`}
        </span>
        <span>p95 {p95}</span>
      </>
    );
  const concurrent = Number(overview.concurrentSessions) || 0;
  const queueDepth = Number(overview.queueDepth) || 0;
  const workerCount = Number(overview.workerCount) || 0;
  const maxConcurrent = Number(overview.maxConcurrent) || 50;
  const availability = overview.agentAvailabilityPct != null
    ? `${overview.agentAvailabilityPct.toFixed(2)}%`
    : "—";
  const spend = overview.spendTotalUsd ?? overview.spendTodayUsd;
  const bd = overview.spendBreakdown;
  const oneMin = overview.oneMinuteCost;
  const oneMinTotal = oneMin?.totalUsd
    ?? (Array.isArray(oneMin?.lines)
      ? oneMin!.lines!.reduce((s, row) => s + (Number(row.usd) || 0), 0)
      : null);
  const oneMinLines = Array.isArray(oneMin?.lines) ? oneMin!.lines! : [];
  const rangeLabel = RANGE_OPTS.find((r) => r.value === range)?.label || range;

  const chart = useMemo(() => {
    const fromApi = overview.charts?.[mode];
    if (fromApi?.pathA) {
      return {
        pathA: fromApi.pathA,
        pathB: fromApi.pathB || fromApi.pathA,
        maxLabel: fromApi.maxLabel || String(fromApi.max || 0),
        legendA: fromApi.legendA || mode,
        legendB: fromApi.legendB || "Trend",
        labels: fromApi.labels || [],
        series: Array.isArray(fromApi.series) ? fromApi.series : [],
        max: Number(fromApi.max) || 0,
      };
    }
    return { ...emptyCharts[mode], series: [] as number[], max: 0 };
  }, [overview.charts, mode]);

  const xLabels = chart.labels?.length
    ? [chart.labels[0], chart.labels[Math.floor(chart.labels.length / 4)], chart.labels[Math.floor(chart.labels.length / 2)], chart.labels[Math.floor((chart.labels.length * 3) / 4)], chart.labels[chart.labels.length - 1]]
    : ["Start", "", "", "", "Now"];

  const storagePipe = findPipe(overview.pipeline, "Storage");
  const storageStat = storagePipe?.note && storagePipe.note !== "offline"
    ? storagePipe.note
    : storagePipe?.samples
      ? `${storagePipe.samples} sessions`
      : "online";

  const groqPipe = findPipe(overview.pipeline, "Groq");
  const groqHealth = overview.providerHealth?.Groq;
  const groqRateEvents = Number(groqHealth?.rateLimitEvents ?? 0);
  const groqDegraded =
    groqPipe?.status === "degraded"
    || groqHealth?.status === "degraded"
    || groqRateEvents > 0;
  const pipelineHealthAction = groqDegraded ? (
    <Pill tone="orange">
      <span className="dot" />
      Groq rate-limited ({groqRateEvents || "?"} events)
    </Pill>
  ) : (
    <Pill tone="green"><span className="dot" />All services healthy</Pill>
  );

  function onChartMove(e: React.MouseEvent<HTMLDivElement>) {
    const series = chart.series || [];
    if (!series.length) {
      setHover(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / Math.max(1, rect.width)));
    const i = Math.min(series.length - 1, Math.round(pct * (series.length - 1)));
    const label = chart.labels?.[i] || `${i}`;
    const a = Number(series[i]) || 0;
    const b = i > 0 ? Number(series[i - 1]) || 0 : a;
    setHover({ i, x: (i / Math.max(1, series.length - 1)) * 100, label, a, b });
  }

  const pipeIcons: Record<string, React.ReactNode> = {
    LiveKit: <RadioTower />,
    Deepgram: <Mic2 />,
    Groq: <BrainCircuit />,
    Cartesia: <Headphones />,
    HeyGen: <Video />,
    Storage: <Cloud />,
  };

  const sourcePill =
    overview.source === "rollup" ? "Rollup"
      : overview.source === "live-fallback" ? "Live fallback"
        : overview.source === "live" ? "Live"
          : "Mock";

  return <>
    <PageHeader
      eyebrow="Live command center"
      title="Dashboard"
      subtitle="Health, capacity, and performance across the realtime stack."
      actions={<>
        <Select
          size="sm"
          variant="toolbar"
          searchable={false}
          ariaLabel="Time range"
          value={range}
          onChange={(v) => setRange((v ?? "24h") as RangeKey)}
          options={RANGE_OPTS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <button
          className="btn"
          type="button"
          disabled={queueing}
          onClick={() => void scheduleRefresh()}
          title="Queue a one-time rollup for this range"
        >
          <RefreshCw className={queueing ? "spin" : ""} />Refresh
        </button>
        <Pill tone={overview.source === "rollup" || overview.source === "live" || overview.source === "live-fallback" ? "green" : "gray"}>
          {sourcePill}
          {queuedHint
            ? " · Queued"
            : overview.generatedAt
              ? ` · ${new Date(overview.generatedAt).toLocaleTimeString()}`
              : loading
                ? " · …"
                : ""}
        </Pill>
      </>}
    />
    <div className="metrics">
      <MetricCard label="Concurrent sessions" value={String(concurrent)} icon={<RadioTower />} foot={<><span className="delta">queue {queueDepth}</span><span>workers {workerCount}</span></>} />
      <MetricCard
        label="Agent availability"
        value={availability}
        icon={<Bot />}
        tone="green"
        foot={<><span className="delta">{(overview.agentAvailabilityPct == null || overview.agentAvailabilityPct >= 99.5) ? "Within SLO" : "Below SLO"}</span><span>{rangeLabel}</span></>}
      />
      <MetricCard
        label="Post-EOS p50"
        value={p50}
        icon={<Zap />}
        tone={latencyTone}
        foot={latencyFoot}
      />
      {canViewFinance ? (
      <div className={`metric metric-orange spend-metric ${spendBreakdown ? "is-breakdown" : ""}`}>
        <div className="metric-top">
          <span>Spend total</span>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              type="button"
              className={`spend-toggle ${spendBreakdown ? "active" : ""}`}
              aria-pressed={spendBreakdown}
              title={spendBreakdown ? "Show total" : "Show breakdown"}
              onClick={() => setSpendBreakdown((v) => !v)}
            >
              <PieChart size={13} />
            </button>
            <span className="metric-icon" style={{ background: "#fff5e7", color: "#e8960d" }}><DollarSign size={14} /></span>
          </span>
        </div>
        {!spendBreakdown ? (
          <>
            <div className="metric-value">{formatSpend(spend)}</div>
            <div className="metric-foot">
              <span className="delta">{bd?.sessionsInRange ?? bd?.sessionsToday ?? overview.sessionsInRange ?? 0} sessions</span>
              <span>{rangeLabel}</span>
            </div>
          </>
        ) : (
          <div className="spend-breakdown">
            <div><span>AWS</span><b>{formatSpend(bd?.awsUsd)}</b></div>
            <div><span>Providers</span><b>{formatSpend(bd?.providersUsd)}</b></div>
            <div><span>Deepgram</span><b>{formatSpend(bd?.deepgramUsd)}</b></div>
            <div><span>Groq</span><b>{formatSpend(bd?.groqUsd)}</b></div>
            <div><span>Cartesia</span><b>{formatSpend(bd?.cartesiaUsd)}</b></div>
            <div><span>LiveKit</span><b>{formatSpend(bd?.livekitUsd)}</b></div>
          </div>
        )}
      </div>
      ) : null}
      {canViewFinance ? (
      <div className={`metric metric-blue spend-metric one-min-metric ${oneMinBreakdown ? "is-breakdown" : ""}`}>
        <div className="metric-top">
          <span>1-min cost</span>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              type="button"
              className={`spend-toggle ${oneMinBreakdown ? "active" : ""}`}
              aria-pressed={oneMinBreakdown}
              title={oneMinBreakdown ? "Show total" : "Show breakdown"}
              onClick={() => setOneMinBreakdown((v) => !v)}
              disabled={!oneMinLines.length}
            >
              <PieChart size={13} />
            </button>
            <span className="metric-icon" style={{ background: "#e8f1ff", color: "#0968f2" }}><Timer size={14} /></span>
          </span>
        </div>
        {!oneMinBreakdown ? (
          <>
            <div className="metric-value">{formatMinuteCost(oneMinTotal)}</div>
            <div className="metric-foot">
              <span className="delta">Fully loaded</span>
              <span>estimate</span>
            </div>
          </>
        ) : (
          <div className="spend-breakdown one-min-breakdown">
            {oneMinLines.map((row) => (
              <div key={row.id}>
                <span title={row.detail || row.label}>{row.label}</span>
                <b>${Number(row.usd).toFixed(4)}</b>
              </div>
            ))}
            <div className="one-min-total"><span>Total</span><b>{formatMinuteCost(oneMinTotal)}</b></div>
          </div>
        )}
      </div>
      ) : null}
    </div>
    <div className="overview-grid">
      <div className="stack">
        <Card
          title="Realtime pipeline health"
          action={pipelineHealthAction}
        >
          <div className="pipeline">
            {overview.pipeline.map((p) => (
              <Pipe
                key={p.name}
                icon={pipeIcons[p.name] || <Cloud />}
                label={p.name}
                tone={pipeTone[p.name] || "livekit"}
                degraded={p.status === "degraded"}
                latency={
                  p.status === "degraded" && p.note
                    ? p.note
                    : p.avgMs != null
                      ? `${Math.round(p.avgMs)}ms avg`
                      : p.p95Ms != null
                        ? `${Math.round(p.p95Ms)}ms p95`
                        : undefined
                }
              />
            ))}
          </div>
        </Card>
        <Card
          title={`${mode} over ${rangeLabel}`}
          kicker={overview.source === "rollup" ? "Precomputed from sessions" : "Waiting for rollup snapshot"}
          action={<div className="segmented">{(["Traffic", "Latency", "Errors"] as const).map((x) => <button key={x} className={mode === x ? "active" : ""} onClick={() => setMode(x)}>{x}</button>)}</div>}
        >
          <div className="chart-legend">
            <span><i style={{ background: "#0968f2" }} />{chart.legendA}</span>
            <span><i style={{ background: "#8057e8" }} />{chart.legendB}</span>
            <span style={{ marginLeft: "auto" }}><b style={{ color: "#334b64" }}>Peak</b> · {chart.maxLabel}</span>
          </div>
          <div
            className="chart-wrap"
            onMouseMove={onChartMove}
            onMouseLeave={() => setHover(null)}
          >
            <div className="chart-grid" />
            <div className="chart-y"><span>{chart.maxLabel}</span><span>75%</span><span>50%</span><span>25%</span><span>0</span></div>
            <svg className="traffic-svg" viewBox="0 0 700 180" preserveAspectRatio="none">
              <defs>
                <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#0968f2" stopOpacity=".22" />
                  <stop offset="1" stopColor="#0968f2" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${chart.pathA} L700 180 L0 180Z`} fill="url(#area)" />
              <path d={chart.pathA} fill="none" stroke="#0968f2" strokeWidth="3" vectorEffect="non-scaling-stroke" />
              <path d={chart.pathB} fill="none" stroke="#8057e8" strokeWidth="2" strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
              {hover && (
                <line
                  x1={(hover.x / 100) * 700}
                  x2={(hover.x / 100) * 700}
                  y1={0}
                  y2={180}
                  stroke="#122b48"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </svg>
            {hover && (
              <div className="chart-tooltip" style={{ left: `calc(28px + ${hover.x}% * 0.96)` }}>
                <b>{hover.label}</b>
                <span>{chart.legendA}: {mode === "Latency" ? `${Math.round(hover.a)}ms` : mode === "Errors" ? `${hover.a}%` : hover.a}</span>
              </div>
            )}
            <div className="chart-x">{xLabels.map((l, i) => <span key={`${l}-${i}`}>{l}</span>)}</div>
          </div>
        </Card>
        <Card title="Open incidents" kicker="Active queue" action={<Link className="btn small" href="/incidents">View incident queue</Link>}>
          <Incident severity="#f59e0b" title="Deepgram latency approaching threshold" meta="INC-2039 · P3 · observing" owner="J. Patel" />
          <Incident severity="#0968f2" title="Storage replication backlog" meta="INC-2036 · P4 · mitigated" owner="S. Novak" />
        </Card>
      </div>
      <div className="stack">
        <Card title="Provider performance" kicker="Current stack">
          <Provider icon="LK" name="LiveKit" note="Media transport" stat={fmtMs(findPipe(overview.pipeline, "LiveKit")?.avgMs)} />
          <Provider icon="DG" name="Deepgram Nova-2" note="Speech recognition" stat={fmtMs(findPipe(overview.pipeline, "Deepgram")?.avgMs)} />
          <Provider
            icon="GQ"
            name="Groq Qwen"
            note={groqDegraded ? (groqPipe?.note || "Rate limited") : "Reasoning model"}
            stat={fmtMs(findPipe(overview.pipeline, "Groq")?.avgMs)}
            tone={groqDegraded ? "warn" : undefined}
          />
          <Provider icon="CA" name="Cartesia Sonic" note="Speech synthesis" stat={fmtMs(findPipe(overview.pipeline, "Cartesia")?.avgMs)} />
          <Provider icon="HG" name="HeyGen Streaming" note="Avatar renderer" stat="off path" />
          <Provider icon="S3" name="S3 Storage" note="Recordings & artifacts" stat={storageStat} />
        </Card>
        <Card title="Capacity" kicker="Live utilization">
          <Capacity name="Concurrent sessions" used={`${concurrent} / ${maxConcurrent}`} width={Math.min(100, (concurrent / maxConcurrent) * 100)} />
          <Capacity name="SQS queue depth" used={String(queueDepth)} width={Math.min(100, queueDepth * 10)} />
          <Capacity name="Bot workers" used={String(workerCount)} width={Math.min(100, workerCount * 5)} color="#0aa878" />
        </Card>
      </div>
    </div>
  </>;
}

function fmtMs(v: number | null | undefined) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Math.round(v)}ms avg`;
}
function findPipe(pipeline: Overview["pipeline"], name: string) {
  return pipeline.find((p) => p.name === name);
}

function Pipe({
  icon,
  label,
  tone,
  latency,
  degraded,
}: {
  icon: React.ReactNode;
  label: string;
  tone: string;
  latency?: string;
  degraded?: boolean;
}) {
  return (
    <div className={`pipe-node pipe-${tone}${degraded ? " pipe-degraded" : ""}`}>
      <span className="pipe-icon">{icon}</span>
      <b>{label}</b>
      <span className="pipe-badge">
        <span className="dot" style={degraded ? { background: "#d97706", boxShadow: "0 0 0 3px rgba(217,119,6,.18)" } : undefined} />
        {latency || (degraded ? "Degraded" : "Healthy")}
      </span>
    </div>
  );
}
function Provider({
  icon,
  name,
  note,
  stat,
  tone,
}: {
  icon: string;
  name: string;
  note: string;
  stat: string;
  tone?: "warn";
}) {
  return (
    <div className="provider-row">
      <span className="provider-icon">{icon}</span>
      <span className="provider-copy">
        <b>{name}</b>
        <span style={tone === "warn" ? { color: "#b45309" } : undefined}>{note}</span>
      </span>
      <span className="provider-stat"><b>{stat}</b><span>live</span></span>
    </div>
  );
}
function Capacity({ name, used, width, color }: { name: string; used: string; width: number; color?: string }) {
  return <div className="capacity-row"><div className="capacity-head"><span>{name}</span><b>{used}</b></div><div className="progress-track"><div className="progress-fill" style={{ width: `${width}%`, background: color }} /></div></div>;
}
function Incident({ severity, title, meta, owner }: { severity: string; title: string; meta: string; owner: string }) {
  return <div className="incident-mini"><span className="severity" style={{ background: severity }} /><span><b>{title}</b><span>{meta}</span></span><Pill tone="gray">{owner}</Pill></div>;
}
